import { BoltIcon } from '@heroicons/react/24/outline';

import { JobSurge } from '../api';
import { formatCents, spanLabel, surgeHolidayNote } from '../format';

import SurgeDialog from './SurgeDialog';

/** One shift that surges the moment it is posted. */
export type PostingSurgeItem = {
  key: string | number;
  /** Which shift, when there are several — "Line Cook · Sat, Jun 20, 9:30 AM". */
  label?: string | null;
  startsAt: string;
  postsAt: string;
  /** Posted now, or a private shift's fallback posting later. */
  postsNow: boolean;
  leadTimeMinutes: number;
  rateCents: number;
  isHoliday?: boolean;
};

type Props = {
  items: PostingSurgeItem[];
  /** "Place order", "Make public" — what the admin is about to do. */
  confirmLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  now?: number;
};

/**
 * The Job page's "Mark as Public" (GRAVY-10881): what posting this job now
 * would apply, from graphy's `post_now_tier`. Tiers are absolute, so on a job
 * re-posted after it already surged, the rise is the step from what is
 * applied. Empty when posting would raise nothing.
 */
export const postNowItems = (
  surge: JobSurge,
  now: number = Date.now(),
): PostingSurgeItem[] => {
  const tier = surge.post_now_tier;
  if (!tier || !surge.first_shift_start_at) return [];
  const rise = Math.min(tier.rate_cents, surge.max_cents) - surge.applied_cents;
  if (rise <= 0) return [];
  return [
    {
      key: surge.job_id,
      startsAt: surge.first_shift_start_at,
      postsAt: new Date(now).toISOString(),
      postsNow: true,
      leadTimeMinutes: tier.lead_time_minutes,
      rateCents: rise,
      isHoliday: surge.is_holiday,
    },
  ];
};

/** More than this and the rest are summed up rather than listed. */
const LISTED = 5;

const minutesBetween = (from: number, to: string) =>
  (new Date(to).getTime() - from) / 60_000;

/*
 * The sentence for one shift, in Product's words (GRAVY-10881): "This shift
 * starts in 1h 30m, inside the 3h surge window — pay will rise by $2.25/hr as
 * soon as it's posted."
 *
 * A private shift with the fallback posting on goes live later, and only if
 * the selected workers have not filled it — so it names when, and how close to
 * the start that leaves it.
 */
const Sentence = ({ item, now }: { item: PostingSurgeItem; now: number }) => {
  const windowLabel = `${spanLabel(item.leadTimeMinutes)} surge window`;
  const amount = <b>{formatCents(item.rateCents)}/hr</b>;

  if (item.postsNow) {
    return (
      <>
        This shift starts in {spanLabel(minutesBetween(now, item.startsAt))},
        inside the {windowLabel} — pay will rise by {amount} as soon as it’s
        posted.
      </>
    );
  }

  const postsIn = spanLabel(minutesBetween(now, item.postsAt));
  const leftAtPosting = spanLabel(
    minutesBetween(new Date(item.postsAt).getTime(), item.startsAt),
  );
  return (
    <>
      If your selected workers don’t fill this shift, it’s posted publicly in{' '}
      {postsIn}, {leftAtPosting} before it starts — inside the {windowLabel}, so
      pay will rise by {amount} as soon as it’s posted.
    </>
  );
};

const HolidayLine = ({ item }: { item: PostingSurgeItem }) =>
  item.isHoliday ? <> {surgeHolidayNote(item.rateCents, 'coming')}</> : null;

/**
 * GRAVY-10881 — tells the admin, before they post, that surge pay will apply
 * straight away because the shift is already inside a tier window.
 *
 * Product's decision (2026-10-06) keeps the behaviour — the next sweep fires
 * every crossed tier at once, at the closest tier's rate — and asks only that
 * it not come as a surprise. Informational: continuing posts exactly as before.
 */
const PostingSurgeDialog = ({
  items,
  confirmLabel,
  busy = false,
  onCancel,
  onConfirm,
  now = Date.now(),
}: Props) => {
  const single = items.length === 1;
  const listed = items.slice(0, LISTED);
  const more = items.length - listed.length;

  return (
    <SurgeDialog
      size="sm"
      testId="postingSurge"
      title="Surge pay will apply right away"
      onClose={onCancel}
    >
      {single ? (
        <p className="paysurge-body" data-testid="postingSurgeMessage">
          <Sentence item={items[0]} now={now} />
          <HolidayLine item={items[0]} />
        </p>
      ) : (
        <>
          <p className="paysurge-body">
            {items.length} shifts start inside a surge window. Pay rises as soon
            as each one is posted:
          </p>
          <ul className="paysurge-postlist" data-testid="postingSurgeList">
            {listed.map((item) => (
              <li key={item.key}>
                <BoltIcon aria-hidden="true" />
                <span>
                  {item.label && (
                    <strong className="paysurge-postlabel">{item.label}</strong>
                  )}
                  <Sentence item={item} now={now} />
                  <HolidayLine item={item} />
                </span>
              </li>
            ))}
          </ul>
          {more > 0 && (
            <p className="paysurge-body" data-testid="postingSurgeMore">
              …and {more} more {more === 1 ? 'shift' : 'shifts'}.
            </p>
          )}
        </>
      )}
      <div className="paysurge-actions">
        <button
          type="button"
          className="paysurge-btn is-outline"
          onClick={onCancel}
        >
          Go back
        </button>
        <button
          type="button"
          className="paysurge-btn is-primary"
          data-testid="postingSurgeConfirm"
          disabled={busy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </SurgeDialog>
  );
};

export default PostingSurgeDialog;
